import countries from 'i18n-iso-countries';
import deutsch from 'i18n-iso-countries/langs/de.json';
import english from 'i18n-iso-countries/langs/en.json';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import {
  FormDatePicker,
  FormSelect,
  FormTextField,
  FormSearchableSelect
} from '../../../../lib/components';
import TranslationHelper from '../shared/translationHelper';
import { ProfileForm, ProfileFormProps } from '../shared/profileForm';
import Util from '../../../../misc/util';
import { EmailOriginEnum } from '@pxtra-client-lib-legacy/gen';
countries.registerLocale(deutsch);
countries.registerLocale(english);

type Props = Omit<ProfileFormProps, 'children'> & {
  userEmailOrigin?: EmailOriginEnum;
};

const PrivateEmailHint: FC<{ userEmailOrigin?: EmailOriginEnum }> = ({ userEmailOrigin }) => {
  const { values } = useFormikContext<{ login?: string }>();
  const { t } = useTranslation();
  if (userEmailOrigin !== 'private') return null;
  const willOverwrite = !!(values.login ?? '').trim();
  return (
    <Grid item xs={6} className="-mt-4 ml-auto">
      <Typography variant="caption" color={willOverwrite ? 'warning.main' : 'text.secondary'}>
        {willOverwrite
          ? t('employee:OVERWRITE_PRIVATE_EMAIL_WARNING')
          : t('employee:PRIVATE_EMAIL_HIDDEN_HINT')}
      </Typography>
    </Grid>
  );
};

export const PersonalDataForm: FC<Props> = (props) => {
  const { t } = useTranslation();
  const helper = new TranslationHelper(t);

  const lng = localStorage.getItem('i18nextLng') || 'en';
  const countryNames = useMemo(
    () => Object.values(countries.getNames(lng)).map((c) => ({ text: c, value: c })),
    [lng]
  );

  const [missingFields, setMissingFields] = useState<string[]>();

  useEffect(() => {
    setMissingFields(props.formRef?.current?.values?.missing_fields);
  }, [props.formRef?.current?.values?.missing_fields]);

  return (
    <ProfileForm {...props}>
      <FormTextField
        placeholder={t('employee:FIRST_NAME')}
        label={t('employee:FIRST_NAME')}
        name="first_name"
        missing={missingFields?.includes('first_name')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormTextField
        placeholder={t('employee:LAST_NAME')}
        label={t('employee:LAST_NAME')}
        name="last_name"
        missing={missingFields?.includes('last_name')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormTextField
        placeholder={t('employee:BIRTH_NAME')}
        label={t('employee:BIRTH_NAME')}
        name="birth_last_name"
        xs={6}
      />
      <FormDatePicker
        label={t('employee:BIRTHDAY')}
        placeholder={t('employee:BIRTHDAY')}
        disableFuture
        minDate={Util.getMinBirthDate()}
        maxDate={new Date()}
        missing={missingFields?.includes('birth_date')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        name="birth_date"
        xs={6}
      />
      <FormSelect
        label={t('employee:GENDER')}
        placeholder={t('employee:GENDER')}
        name="gender"
        options={helper.gender.options}
        xs={6}
      />
      <FormSelect
        label={t('employee:MARRIAGE_STATUS_QUESTION')}
        placeholder={t('employee:MARRIAGE_STATUS_QUESTION')}
        name="marriage_status"
        options={helper.marriageStatus.options}
        missing={missingFields?.includes('marriage_status')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormSearchableSelect
        label={t('employee:NATIONALITY')}
        placeholder={t('employee:NATIONALITY')}
        name="nationality"
        options={countryNames}
        missing={missingFields?.includes('nationality')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormTextField
        label={t('account:LOGIN_EMAIL_FIELD')}
        placeholder={t('auth:email')}
        name="login"
        xs={6}
      />
      <PrivateEmailHint userEmailOrigin={props.userEmailOrigin} />
    </ProfileForm>
  );
};
